import {
  Badge,
  Button,
  Spinner,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import {
  ArrowDownloadRegular,
  ArrowSyncRegular,
  ArrowUploadRegular,
  CheckmarkRegular,
  DeleteRegular,
  EditRegular,
  OpenRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import { DocumentIcon } from "icons/DocumentIcon";
import { useWorkspaceDiagnostic } from "apps/project-iq/hooks/useWorkspaceDiagnostic";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import {
  DiagnosticMethod,
  IResultFile,
} from "apps/project-iq/models/IDiagnostic";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { IUser } from "models/IUser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  addDiagnosticResultFile,
  deleteDiagnosticResultFile,
  replaceDiagnosticResultFile,
  getDownloadSignedUrl,
  saveDiagnosticSummary,
  setDiagnosticStatus,
} from "api";
import {
  addProjectIqWorkspaceDocument,
  generateDiagnosticSummary,
  getWorkspaceAnalyticsForDiagnostic,
} from "apps/project-iq/api";
import { CreateAnalyticsDialog } from "apps/project-iq/components/CreateAnalyticsDialog/CreateAnalyticsDialog";
import { AnalyticsRun } from "apps/project-iq/pages/AnalyticsDetailPage/AnalyticsRun";
import { AnalyticsResults } from "apps/project-iq/pages/AnalyticsDetailPage/AnalyticsResults";
import { useAnalyticsProjectPolling } from "apps/project-iq/pages/AnalyticsDetailPage/useAnalyticsProjectPolling";
import { ProjectStatus } from "apps/decooda/components/projects/projectConstants";
import { FileWithId } from "models/FileWithId";
import { uploadFilesToStorage } from "utils/blobUtils";
import {
  formatFileSize,
  getFileTypeInfo,
} from "apps/project-iq/utils/fileUtils";
import { useDiagnosticResultFilesStyles } from "./DiagnosticResultFiles.styles";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { IDiagnosticResultFilesProps } from "./DiagnosticResultFiles.types";

type AnalyticsProjectState = "loading" | "idle" | "active";
type AnalyticsTab = "run" | "results";
const ANALYTICS_TABS: { key: AnalyticsTab; label: string }[] = [
  { key: "run", label: "Run" },
  { key: "results", label: "Results" },
];

const isAnalyticsEligibleMethod = (
  method: DiagnosticMethod | undefined,
): boolean =>
  method === "workshops_focus_groups" ||
  method === "in_person_interviews" ||
  method === "survey";

const CLAUDE_URL = "https://claude.ai";
const COPILOT_URL = "https://m365.cloud.microsoft/chat/";

const ACCEPTED_FILE_TYPES =
  ".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.csv,.png,.jpg,.jpeg";
const ENTITY_TYPE = "diagnostics_results";
const formatUploadDate = (timestamp: number): string =>
  new Date(timestamp * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

interface IUploadingFile {
  id: string;
  originalFilename: string;
  status: "uploading" | "error";
}

interface IFilePendingReplace {
  newFile: File;
  existingFile: IResultFile;
}

function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/^-{3,}$/gm, "")
    .replace(/`(.+?)`/g, "$1")
    .trim();
}

export const DiagnosticResultFiles = ({
  diagnosticId,
  workspaceId,
}: IDiagnosticResultFilesProps): JSX.Element => {
  const styles = useDiagnosticResultFilesStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const queryClient = useQueryClient();

  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  ) as IUser | null;

  const workspace = useSelector((state: RootState) =>
    state.projectIqWorkspacesSlice.value.find((w) => w.id === workspaceId),
  ) as IProjectIQWorkspace | undefined;

  const canEdit = useMemo(
    () =>
      (workspace?.owners?.some((owner) => owner.userId === currentUser?.id) ||
        workspace?.teamMembers?.some(
          (member) => member.userId === currentUser?.id,
        )) ??
      false,
    [workspace?.owners, workspace?.teamMembers, currentUser?.id],
  );

  const {
    data: diagnostic,
    isLoading,
    isFetching,
  } = useWorkspaceDiagnostic(diagnosticId);

  const resultFiles = useMemo(
    () => diagnostic?.resultFiles ?? [],
    [diagnostic?.resultFiles],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const fileBeingReplacedRef = useRef<IResultFile | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<IUploadingFile[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const hasActiveUploads = uploadingFiles.some((f) => f.status === "uploading");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<IResultFile | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [replacingUrls, setReplacingUrls] = useState<Set<string>>(new Set());
  const [filesPendingReplace, setFilesPendingReplace] = useState<
    IFilePendingReplace[]
  >([]);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [summaryText, setSummaryText] = useState<string>("");
  const [lastSavedSummary, setLastSavedSummary] = useState<string>("");
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const hasSummaryChanges = summaryText !== lastSavedSummary;
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [draftState, setDraftState] = useState<
    "idle" | "generating" | "ready" | "error"
  >("idle");
  const [draft, setDraft] = useState("");
  const [progressSteps, setProgressSteps] = useState<string[]>([]);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [analyticsProjectId, setAnalyticsProjectId] = useState<string | null>(
    null,
  );
  const [analyticsProjectState, setAnalyticsProjectState] =
    useState<AnalyticsProjectState>("loading");
  const [activeAnalyticsTab, setActiveAnalyticsTab] =
    useState<AnalyticsTab>("run");
  const analyticsTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [isEditAnalyticsDialogOpen, setIsEditAnalyticsDialogOpen] =
    useState(false);
  const [analyticsLookupError, setAnalyticsLookupError] = useState<
    string | null
  >(null);
  const analyticsPanelHeadingRef = useRef<HTMLHeadingElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const saved = diagnostic?.summary ?? "";
    setSummaryText(saved);
    setLastSavedSummary(saved);
  }, [diagnostic?.summary]);

  const checkForLinkedAnalyticsProject = useCallback(async () => {
    if (!diagnosticId || !workspaceId) return;

    setAnalyticsLookupError(null);
    setAnalyticsProjectState("loading");
    try {
      const linkedProjects = await getWorkspaceAnalyticsForDiagnostic({
        workspaceId,
        diagnosticId,
      });
      if (!isMountedRef.current) return;

      const existingProject = linkedProjects?.[0];
      if (existingProject) {
        setAnalyticsProjectId(existingProject.id);
        setAnalyticsProjectState("active");
        setActiveAnalyticsTab(
          existingProject.status === ProjectStatus.Scored ? "results" : "run",
        );
      } else {
        setAnalyticsProjectState("idle");
      }
    } catch {
      if (!isMountedRef.current) return;

      setAnalyticsProjectState("idle");
      setAnalyticsLookupError(
        "Couldn't check whether this diagnostic already has an AI Analytics project. You can retry, or create a new one below.",
      );
    }
  }, [diagnosticId, workspaceId]);

  useEffect(() => {
    if (!isAnalyticsEligibleMethod(diagnostic?.method)) return;
    void checkForLinkedAnalyticsProject();
  }, [diagnostic?.method, checkForLinkedAnalyticsProject]);

  const {
    project: analyticsProject,
    isPendingScore: isAnalyticsPendingScore,
    handleProjectUpdated: handleAnalyticsProjectUpdated,
    handleScoringInitiated: handleAnalyticsScoringInitiated,
  } = useAnalyticsProjectPolling(analyticsProjectId ?? "");

  const focusAnalyticsPanelHeading = useCallback(() => {
    setTimeout(() => analyticsPanelHeadingRef.current?.focus(), 0);
  }, []);

  const handleOpenAnalyticsDialog = useCallback(() => {
    setIsAnalyticsDialogOpen(true);
  }, []);

  const handleAnalyticsCreated = useCallback(
    (projectId: string) => {
      setAnalyticsProjectId(projectId);
      setAnalyticsProjectState("active");
      setActiveAnalyticsTab("run");
      setIsAnalyticsDialogOpen(false);
      focusAnalyticsPanelHeading();
    },
    [focusAnalyticsPanelHeading],
  );

  const handleAnalyticsScoringInitiatedWrapped = useCallback(() => {
    handleAnalyticsScoringInitiated();
    focusAnalyticsPanelHeading();
  }, [handleAnalyticsScoringInitiated, focusAnalyticsPanelHeading]);

  const handleViewAnalyticsResults = useCallback(() => {
    setActiveAnalyticsTab("results");
    focusAnalyticsPanelHeading();
  }, [focusAnalyticsPanelHeading]);

  const handleAnalyticsTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = ANALYTICS_TABS.findIndex(
        (tab) => tab.key === activeAnalyticsTab,
      );
      let nextIndex: number | null = null;
      if (event.key === "ArrowRight")
        nextIndex = (currentIndex + 1) % ANALYTICS_TABS.length;
      else if (event.key === "ArrowLeft")
        nextIndex =
          (currentIndex - 1 + ANALYTICS_TABS.length) % ANALYTICS_TABS.length;
      if (nextIndex !== null) {
        event.preventDefault();
        setActiveAnalyticsTab(ANALYTICS_TABS[nextIndex].key);
        analyticsTabRefs.current[nextIndex]?.focus();
      }
    },
    [activeAnalyticsTab],
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const processFiles = useCallback(
    async (files: FileList) => {
      if (!files || files.length === 0) return;

      setUploadError(null);

      const conflicts: IFilePendingReplace[] = [];
      const direct: File[] = [];

      Array.from(files).forEach((file) => {
        const existing = resultFiles.find(
          (rf) => rf.filename.toLowerCase() === file.name.toLowerCase(),
        );
        if (existing) {
          conflicts.push({ newFile: file, existingFile: existing });
        } else {
          direct.push(file);
        }
      });

      if (conflicts.length > 0) {
        setFilesPendingReplace((prev) => [...prev, ...conflicts]);
      }

      if (direct.length === 0) return;

      const newUploadingFiles: IUploadingFile[] = direct.map((file) => ({
        id: crypto.randomUUID(),
        originalFilename: file.name,
        status: "uploading" as const,
      }));

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      try {
        await Promise.all(
          direct.map(async (file, index) => {
            const uploadId = newUploadingFiles[index].id;
            try {
              const fileWithId: FileWithId = { file, id: uploadId };
              const uploadedFiles = await uploadFilesToStorage(
                [fileWithId],
                ENTITY_TYPE,
                diagnosticId,
              );

              if (uploadedFiles.length === 0) {
                setUploadingFiles((prev) =>
                  prev.map((f) =>
                    f.id === uploadId ? { ...f, status: "error" as const } : f,
                  ),
                );
                return;
              }

              const uploaded = uploadedFiles[0];
              await addDiagnosticResultFile({
                diagnosticId,
                file: {
                  filename: uploaded.filename,
                  url: uploaded.url ?? "",
                  contentType: file.type || "application/octet-stream",
                  size: file.size,
                  uploadedAt: Date.now(),
                },
              });

              try {
                const wsUploaded = await uploadFilesToStorage(
                  [{ file, id: crypto.randomUUID() }],
                  "workspace_library",
                  workspaceId,
                );
                if (wsUploaded.length > 0) {
                  await addProjectIqWorkspaceDocument({
                    workspaceId,
                    filename: wsUploaded[0].filename,
                    originalFilename: file.name,
                    source: "file",
                  });
                }
              } catch {
                setUploadError(
                  "File saved, but could not add to workspace context. You can add it manually from the Workspace Files tab.",
                );
              }

              setUploadingFiles((prev) =>
                prev.filter((f) => f.id !== uploadId),
              );
            } catch {
              setUploadingFiles((prev) =>
                prev.map((f) =>
                  f.id === uploadId ? { ...f, status: "error" as const } : f,
                ),
              );
            }
          }),
        );
      } finally {
        queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [diagnosticId, queryClient, resultFiles, workspaceId],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files;
      await processFiles(selectedFiles as FileList);
    },
    [processFiles],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    [],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingOver(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
    },
    [],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      await processFiles(event.dataTransfer.files);
    },
    [processFiles],
  );

  const handleDropZoneKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleUploadClick();
      }
    },
    [handleUploadClick],
  );

  const handleDownload = useCallback(async (file: IResultFile) => {
    setDownloadError(null);
    try {
      const result = await getDownloadSignedUrl({ filename: file.url });
      if (result?.sas) {
        window.open(result.sas, "_blank");
      } else {
        setDownloadError("Download failed. Please try again.");
      }
    } catch {
      setDownloadError("Download failed. Please try again.");
    }
  }, []);

  const handleDeleteRequest = useCallback((file: IResultFile) => {
    setFileToDelete(file);
    setDeleteError(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!fileToDelete) return;
    setDeleteError(null);
    await deleteDiagnosticResultFile({
      diagnosticId,
      url: fileToDelete.url,
    });
    queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
    setFileToDelete(null);
    setTimeout(() => dropZoneRef.current?.focus(), 0);
  }, [fileToDelete, diagnosticId, queryClient]);

  const handleDeleteDismiss = useCallback(() => {
    setFileToDelete(null);
    setDeleteError(null);
  }, []);

  const handleReplaceConfirm = useCallback(async () => {
    const pending = filesPendingReplace[0];
    if (!pending) return;

    setFilesPendingReplace((prev) => prev.slice(1));

    const oldUrl = pending.existingFile.url;
    setUploadError(null);
    setReplacingUrls((prev) => new Set(prev).add(oldUrl));

    try {
      const fileWithId: FileWithId = {
        file: pending.newFile,
        id: crypto.randomUUID(),
      };
      const uploadedFiles = await uploadFilesToStorage(
        [fileWithId],
        ENTITY_TYPE,
        diagnosticId,
      );
      if (uploadedFiles.length === 0) throw new Error("Upload failed");

      const uploaded = uploadedFiles[0];
      await replaceDiagnosticResultFile({
        diagnosticId,
        oldUrl,
        file: {
          filename: uploaded.filename,
          url: uploaded.url ?? "",
          contentType: pending.newFile.type || "application/octet-stream",
          size: pending.newFile.size,
        },
      });
    } catch {
      setUploadError("Failed to replace file. Please try again.");
    } finally {
      setReplacingUrls((prev) => {
        const next = new Set(prev);
        next.delete(oldUrl);
        return next;
      });
      queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
    }
  }, [filesPendingReplace, diagnosticId, queryClient]);

  const handleReplaceDismiss = useCallback(() => {
    setFilesPendingReplace((prev) => prev.slice(1));
  }, []);

  const handleReplaceClick = useCallback((file: IResultFile) => {
    fileBeingReplacedRef.current = file;
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      const fileToReplace = fileBeingReplacedRef.current;
      if (!selectedFile || !fileToReplace) return;

      const oldUrl = fileToReplace.url;
      fileBeingReplacedRef.current = null;
      setUploadError(null);
      setReplacingUrls((prev) => new Set(prev).add(oldUrl));

      try {
        const fileWithId: FileWithId = {
          file: selectedFile,
          id: crypto.randomUUID(),
        };
        const uploadedFiles = await uploadFilesToStorage(
          [fileWithId],
          ENTITY_TYPE,
          diagnosticId,
        );

        if (uploadedFiles.length === 0) {
          throw new Error("Upload failed");
        }

        const uploaded = uploadedFiles[0];
        await replaceDiagnosticResultFile({
          diagnosticId,
          oldUrl,
          file: {
            filename: uploaded.filename,
            url: uploaded.url ?? "",
            contentType: selectedFile.type || "application/octet-stream",
            size: selectedFile.size,
          },
        });
      } catch {
        setUploadError("Failed to replace file. Please try again.");
      } finally {
        setReplacingUrls((prev) => {
          const next = new Set(prev);
          next.delete(oldUrl);
          return next;
        });
        queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
        if (replaceInputRef.current) {
          replaceInputRef.current.value = "";
        }
      }
    },
    [diagnosticId, queryClient],
  );

  const handleSaveSummary = useCallback(async () => {
    setIsSavingSummary(true);
    setSummaryError(null);
    try {
      await saveDiagnosticSummary({ diagnosticId, summary: summaryText });
      setLastSavedSummary(summaryText);
      queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
    } catch {
      setSummaryError("Failed to save summary. Please try again.");
    } finally {
      setIsSavingSummary(false);
    }
  }, [diagnosticId, summaryText, queryClient]);

  const handleGenerateSummary = useCallback(async () => {
    setDraftState("generating");
    setProgressSteps([]);
    setDraft("");
    setDraftError(null);

    try {
      const response = await generateDiagnosticSummary(diagnosticId);

      if (!response.ok) {
        setDraftError("Failed to generate summary. Please try again.");
        setDraftState("error");
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        setDraftError("Unable to read response stream.");
        setDraftState("error");
        return;
      }

      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);

            if (data.progress) {
              setProgressSteps((prev) => [...prev, data.progress]);
            }

            if (data.content && !data.hasFinished) {
              accumulated += data.content;
            }

            if (data.hasFinished && !data.error) {
              setDraft(stripMarkdown(accumulated));
              setDraftState("ready");
            }

            if (data.error) {
              setDraftError(data.error);
              setDraftState("error");
              return;
            }
          } catch (e) {
            console.error(
              "[GenerateAISummary] Failed to parse stream chunk:",
              e,
            );
          }
        }
      }

      const finalChunk = decoder.decode();
      if (finalChunk.trim()) {
        const lines = finalChunk.split("\n").filter((line) => line.trim());
        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            if (data.hasFinished && !data.error) {
              setDraft(stripMarkdown(accumulated));
              setDraftState("ready");
            }
          } catch (e) {
            console.error(
              "[GenerateAISummary] Failed to parse stream chunk:",
              e,
            );
          }
        }
      }
    } catch (error) {
      setDraftError(
        error instanceof Error
          ? error.message
          : "An error occurred while generating the summary.",
      );
      setDraftState("error");
    }
  }, [diagnosticId]);

  const handleInsertDraft = useCallback(() => {
    setSummaryText(draft);
    setDraftState("idle");
  }, [draft]);

  const handleDiscardDraft = useCallback(() => {
    setDraftState("idle");
    setDraft("");
    setProgressSteps([]);
  }, []);

  const handleMarkComplete = useCallback(async () => {
    try {
      await setDiagnosticStatus({ diagnosticId, status: "completed" });
      queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
    } catch {
      setSummaryError("Failed to mark diagnostic complete. Please try again.");
    }
  }, [diagnosticId, queryClient]);

  const handleReopen = useCallback(async () => {
    try {
      await setDiagnosticStatus({ diagnosticId, status: "in_progress" });
      queryClient.invalidateQueries(["workspace-diagnostic", diagnosticId]);
    } catch {
      setSummaryError("Failed to reopen diagnostic. Please try again.");
    }
  }, [diagnosticId, queryClient]);

  const iconStyleMap: Record<string, string> = {
    pdf: styles.fileIconPdf,
    xls: styles.fileIconXls,
    ppt: styles.fileIconPpt,
    default: styles.fileIconDefault,
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerRoot}>
        <Spinner size="medium" label="Loading result files..." />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Results</h2>
        {diagnostic?.status === "completed" && (
          <span className={styles.completeBadge} aria-label="Status: Complete">
            ✓ COMPLETE
          </span>
        )}
      </div>
      <p className={styles.subtitle}>
        Capture the outcome of this diagnostic — a summary, supporting files,
        and a jump-off point into AI Analytics.
      </p>

      {uploadError && (
        <p className={styles.errorMessage} role="alert">
          {uploadError}
        </p>
      )}

      {downloadError && (
        <p className={styles.errorMessage} role="alert">
          {downloadError}
        </p>
      )}

      <div className={styles.summarySection}>
        <p className={styles.sectionLabel}>Summary of Findings</p>
        <textarea
          className={styles.summaryTextarea}
          placeholder="Document the outcomes, key themes and headline findings from this diagnostic..."
          value={summaryText}
          onChange={(e) => setSummaryText(e.target.value)}
          aria-label="Summary of findings"
          disabled={!canEdit}
        />
        {summaryError && (
          <p className={styles.errorMessage} role="alert">
            {summaryError}
          </p>
        )}
        {canEdit && (
          <div className={styles.summaryActions}>
            <Button
              appearance="primary"
              className={sharedButtonStyles.primaryButton}
              onClick={handleSaveSummary}
              disabled={isSavingSummary || !hasSummaryChanges}
            >
              {isSavingSummary ? "Saving..." : "Save summary"}
            </Button>
          </div>
        )}
      </div>

      {canEdit && (
        <>
          <p className={styles.sectionLabel}>Supporting Files</p>
          <div
            ref={dropZoneRef}
            className={mergeClasses(
              styles.dropZone,
              isDraggingOver && styles.dropZoneActive,
              hasActiveUploads && styles.dropZoneDisabled,
            )}
            role="button"
            tabIndex={0}
            aria-label="Drop files here or click to upload"
            onClick={handleUploadClick}
            onKeyDown={handleDropZoneKeyDown}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <ArrowUploadRegular
              className={styles.dropZoneIcon}
              aria-hidden="true"
            />
            <p className={styles.dropZoneTitle}>
              Drop files here or click to upload
            </p>
            <p className={styles.dropZoneHint}>
              Upload any output from this diagnostic run — transcripts, notes,
              exports, deliverables.
              <br />
              Files added here are saved as context within the workspace and
              accessible for all workspace members.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            aria-label="Select result files to upload"
            className={styles.hiddenInput}
            onChange={handleFileSelect}
          />
          <input
            ref={replaceInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            aria-label="Select replacement file"
            className={styles.hiddenInput}
            onChange={handleReplaceFileSelect}
          />
        </>
      )}

      {!canEdit &&
        resultFiles.length === 0 &&
        uploadingFiles.length === 0 &&
        !isFetching && (
          <p className={styles.emptyStateNonOwner}>
            No result files have been uploaded yet.
          </p>
        )}

      {(uploadingFiles.length > 0 || resultFiles.length > 0) && (
        <ul className={styles.fileList} aria-label="Result files">
          {uploadingFiles.map((uploadFile) => {
            const uploadTypeInfo = getFileTypeInfo(uploadFile.originalFilename);
            return (
              <li
                key={uploadFile.id}
                className={mergeClasses(
                  styles.fileRow,
                  styles.fileRowUploading,
                )}
              >
                <div
                  className={mergeClasses(
                    styles.fileIconWrapper,
                    iconStyleMap[uploadTypeInfo.style] ??
                      styles.fileIconDefault,
                  )}
                  aria-hidden="true"
                >
                  <DocumentIcon size={20} />
                </div>
                <div className={styles.fileInfo}>
                  <span className={styles.fileName}>
                    {uploadFile.originalFilename}
                  </span>
                  <span className={styles.fileMeta}>
                    {uploadFile.status === "uploading"
                      ? "Uploading..."
                      : "Upload failed"}
                  </span>
                </div>
                {uploadFile.status === "uploading" && (
                  <Spinner
                    size="tiny"
                    className={styles.fileSpinner}
                    aria-label={`Uploading ${uploadFile.originalFilename}`}
                  />
                )}
              </li>
            );
          })}
          {resultFiles.map((file) => {
            const typeInfo = getFileTypeInfo(file.filename);
            const isReplacing = replacingUrls.has(file.url);
            return (
              <li
                key={file.filename}
                className={mergeClasses(
                  styles.fileRow,
                  isReplacing && styles.fileRowUploading,
                )}
              >
                <div
                  className={mergeClasses(
                    styles.fileIconWrapper,
                    iconStyleMap[typeInfo.style] ?? styles.fileIconDefault,
                  )}
                  aria-hidden="true"
                >
                  <DocumentIcon size={20} />
                </div>
                <div className={styles.fileInfo}>
                  <p className={styles.fileName}>{file.filename}</p>
                  <p className={styles.fileMeta}>
                    {typeInfo.label}
                    {file.size != null && ` · ${formatFileSize(file.size)}`}
                    {file.uploadedAt != null &&
                      ` · ${formatUploadDate(file.uploadedAt)}`}
                  </p>
                </div>
                <div className={styles.actions}>
                  {isReplacing ? (
                    <Spinner
                      size="tiny"
                      className={styles.fileSpinner}
                      aria-label={`Replacing ${file.filename}`}
                    />
                  ) : (
                    <>
                      <Tooltip content="Download file" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<ArrowDownloadRegular aria-hidden="true" />}
                          aria-label={`Download ${file.filename}`}
                          onClick={() => handleDownload(file)}
                        />
                      </Tooltip>
                      {canEdit && (
                        <>
                          <Tooltip content="Replace file" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<ArrowSyncRegular aria-hidden="true" />}
                              aria-label={`Replace ${file.filename}`}
                              onClick={() => handleReplaceClick(file)}
                            />
                          </Tooltip>
                          <Tooltip content="Delete file" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<DeleteRegular aria-hidden="true" />}
                              aria-label={`Delete ${file.filename}`}
                              onClick={() => handleDeleteRequest(file)}
                            />
                          </Tooltip>
                        </>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.furtherAnalysisSection}>
        <p className={styles.sectionLabel}>Further Analysis</p>
        {diagnostic?.method === "ai_interview" ? (
          <>
            {draftState === "idle" && (
              <div
                className={styles.analysisCard}
                onClick={handleGenerateSummary}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleGenerateSummary()}
                aria-label="Generate AI Summary"
              >
                <div
                  className={styles.analysisIconContainer}
                  aria-hidden="true"
                >
                  <SparkleRegular />
                </div>
                <div className={styles.analysisContent}>
                  <p className={styles.analysisTitle}>Generate AI Summary</p>
                  <p className={styles.analysisSubtitle}>
                    Reads this diagnostic's background, objectives, and every
                    collected response, then drafts a findings summary.
                  </p>
                </div>
                <Badge appearance="filled" color="brand" size="small">
                  NEW
                </Badge>
              </div>
            )}
            {draftState === "generating" && (
              <div className={styles.draftPanel}>
                <div className={styles.analysisCardHeader}>
                  <div
                    className={styles.analysisIconContainer}
                    aria-hidden="true"
                  >
                    <SparkleRegular />
                  </div>
                  <span className={styles.analysisTitle}>
                    Generate AI Summary
                  </span>
                </div>
                <ul className={styles.progressList}>
                  {progressSteps.length === 0 && (
                    <li className={styles.progressStep}>
                      <Spinner size="tiny" />
                      <span>Starting...</span>
                    </li>
                  )}
                  {progressSteps.map((step, i) => (
                    <li key={i} className={styles.progressStep}>
                      {i === progressSteps.length - 1 ? (
                        <Spinner size="tiny" />
                      ) : (
                        <CheckmarkRegular
                          style={{ color: projectIqTokens.teal }}
                        />
                      )}
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {draftState === "ready" && (
              <div className={styles.draftPanel}>
                <div className={styles.analysisCardHeader}>
                  <div
                    className={styles.analysisIconContainer}
                    aria-hidden="true"
                  >
                    <SparkleRegular />
                  </div>
                  <span className={styles.analysisTitle}>
                    Generate AI Summary
                  </span>
                  <span className={styles.analysisSubtitle}>
                    Draft ready below.
                  </span>
                </div>
                <div className={styles.draftScroll}>{draft}</div>
                <div className={styles.draftActions}>
                  <Button appearance="primary" onClick={handleInsertDraft}>
                    Insert into Summary of Findings
                  </Button>
                  <Button appearance="subtle" onClick={handleDiscardDraft}>
                    Discard
                  </Button>
                </div>
              </div>
            )}
            {draftState === "error" && (
              <div className={styles.draftPanel}>
                <div className={styles.analysisCardHeader}>
                  <div
                    className={styles.analysisIconContainer}
                    aria-hidden="true"
                  >
                    <SparkleRegular />
                  </div>
                  <span className={styles.analysisTitle}>
                    Generate AI Summary
                  </span>
                </div>
                <p className={styles.draftError}>{draftError}</p>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={handleGenerateSummary}
                >
                  Try again
                </Button>
              </div>
            )}
          </>
        ) : isAnalyticsEligibleMethod(diagnostic?.method) ? (
          <div aria-live="polite" aria-atomic="false">
            {analyticsLookupError && (
              <div className={styles.analysisLookupError} role="alert">
                <span>{analyticsLookupError}</span>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => void checkForLinkedAnalyticsProject()}
                >
                  Retry
                </Button>
              </div>
            )}
            {analyticsProjectState === "loading" ? (
              <div className={styles.analysisLoading}>
                <Spinner
                  size="tiny"
                  label="Checking for a linked AI Analytics project..."
                />
              </div>
            ) : analyticsProjectState === "idle" ? (
              <div
                className={styles.analysisCard}
                onClick={handleOpenAnalyticsDialog}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleOpenAnalyticsDialog()
                }
                aria-label="Analyze in AI Analytics"
              >
                <div
                  className={styles.analysisIconContainer}
                  aria-hidden="true"
                >
                  <SparkleRegular />
                </div>
                <div className={styles.analysisContent}>
                  <p className={styles.analysisTitle}>
                    Analyze in AI Analytics
                  </p>
                  <p className={styles.analysisSubtitle}>
                    Set up an AI Analytics project for this workspace right here
                    — pick a workspace file and run{" "}
                    <em>topic identification</em> to surface recurring themes
                    and <em>impact analysis</em> to score and prioritise
                    findings.
                  </p>
                </div>
              </div>
            ) : (
              <div className={styles.draftPanel}>
                <div className={styles.analysisCardHeader}>
                  <div
                    className={styles.analysisIconContainer}
                    aria-hidden="true"
                  >
                    <SparkleRegular />
                  </div>
                  <h3
                    ref={analyticsPanelHeadingRef}
                    tabIndex={-1}
                    className={styles.analysisTitle}
                  >
                    Analyze in AI Analytics
                  </h3>
                  {analyticsProject && (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular aria-hidden="true" />}
                      onClick={() => setIsEditAnalyticsDialogOpen(true)}
                    >
                      Edit data file
                    </Button>
                  )}
                </div>
                {!analyticsProject ? (
                  <Spinner size="tiny" label="Loading analytics project..." />
                ) : (
                  <>
                    <div
                      className={styles.analyticsTabBar}
                      role="tablist"
                      aria-label="AI Analytics sections"
                    >
                      {ANALYTICS_TABS.map((tab, index) => {
                        const isActive = tab.key === activeAnalyticsTab;
                        return (
                          <button
                            key={tab.key}
                            ref={(el) => {
                              analyticsTabRefs.current[index] = el;
                            }}
                            id={`analytics-tab-${tab.key}`}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`analytics-tabpanel-${tab.key}`}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => setActiveAnalyticsTab(tab.key)}
                            onKeyDown={handleAnalyticsTabKeyDown}
                            className={mergeClasses(
                              styles.analyticsTab,
                              isActive && styles.analyticsTabActive,
                            )}
                          >
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                    <div
                      id={`analytics-tabpanel-${activeAnalyticsTab}`}
                      role="tabpanel"
                      aria-labelledby={`analytics-tab-${activeAnalyticsTab}`}
                    >
                      {activeAnalyticsTab === "results" ? (
                        <AnalyticsResults
                          project={analyticsProject}
                          analyticsId={analyticsProjectId ?? ""}
                          workspaceId={workspaceId}
                        />
                      ) : (
                        <AnalyticsRun
                          project={analyticsProject}
                          isPendingScore={isAnalyticsPendingScore}
                          onViewResults={handleViewAnalyticsResults}
                          onProjectUpdated={handleAnalyticsProjectUpdated}
                          onScoringInitiated={
                            handleAnalyticsScoringInitiatedWrapped
                          }
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : null}
        <a
          href={CLAUDE_URL}
          target="_blank"
          rel="noreferrer"
          className={styles.analysisCard}
          aria-label="Analyze in Claude (opens in new tab)"
        >
          <div className={styles.analysisIconContainer} aria-hidden="true">
            <SparkleRegular />
          </div>
          <div className={styles.analysisContent}>
            <p className={styles.analysisTitle}>Analyze in Claude</p>
            <p className={styles.analysisSubtitle}>
              Open Claude in a new tab to analyze your findings with AI
              assistance.
            </p>
          </div>
          <div className={styles.analysisArrow} aria-hidden="true">
            <OpenRegular />
          </div>
        </a>
        <a
          href={COPILOT_URL}
          target="_blank"
          rel="noreferrer"
          className={styles.analysisCard}
          aria-label="Analyze in Copilot (opens in new tab)"
        >
          <div className={styles.analysisIconContainer} aria-hidden="true">
            <SparkleRegular />
          </div>
          <div className={styles.analysisContent}>
            <p className={styles.analysisTitle}>Analyze in Copilot</p>
            <p className={styles.analysisSubtitle}>
              Open Copilot Chat in a new tab to analyze your findings with
              Microsoft AI.
            </p>
          </div>
          <div className={styles.analysisArrow} aria-hidden="true">
            <OpenRegular />
          </div>
        </a>
      </div>

      {isAnalyticsDialogOpen && workspace && (
        <CreateAnalyticsDialog
          isOpen={isAnalyticsDialogOpen}
          workspaceId={workspaceId}
          workspace={workspace}
          mode="create"
          diagnosticId={diagnosticId}
          onCreated={handleAnalyticsCreated}
          onDismiss={() => setIsAnalyticsDialogOpen(false)}
        />
      )}

      {isEditAnalyticsDialogOpen && workspace && analyticsProject && (
        <CreateAnalyticsDialog
          isOpen={isEditAnalyticsDialogOpen}
          workspaceId={workspaceId}
          workspace={workspace}
          mode="edit"
          project={analyticsProject}
          onSaved={handleAnalyticsProjectUpdated}
          onDismiss={() => setIsEditAnalyticsDialogOpen(false)}
        />
      )}

      <hr className={styles.divider} />

      <div className={styles.completeRow}>
        {diagnostic?.status === "completed" ? (
          <>
            <p className={styles.completeHint}>
              Diagnostic is complete and will show as completed on the main
              list.
            </p>
            <Button
              className={sharedButtonStyles.secondaryButton}
              onClick={handleReopen}
            >
              Reopen
            </Button>
          </>
        ) : (
          <>
            <p className={styles.completeHint}>
              {diagnostic?.status === "in_progress"
                ? "Once results are documented, mark the diagnostic as complete."
                : "Complete the Run step first — mark data as collected before closing out the diagnostic."}
            </p>
            <Button
              appearance="primary"
              className={sharedButtonStyles.primaryButton}
              disabled={diagnostic?.status !== "in_progress"}
              onClick={handleMarkComplete}
            >
              Mark diagnostic complete
            </Button>
          </>
        )}
      </div>

      <ActionDialog
        isVisible={fileToDelete !== null}
        title="Delete file"
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{fileToDelete?.filename}</strong>? This action cannot be
            undone.
            {deleteError && (
              <p className={styles.errorMessage} role="alert">
                {deleteError}
              </p>
            )}
          </>
        }
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleDeleteConfirm}
        onDismiss={handleDeleteDismiss}
      />

      <ActionDialog
        isVisible={filesPendingReplace.length > 0}
        title="Replace file"
        message={
          <>
            A file named{" "}
            <strong>{filesPendingReplace[0]?.existingFile.filename}</strong>{" "}
            already exists. Do you want to replace it with the new version?
          </>
        }
        confirmationMessage="Replace"
        dismissMessage="Cancel"
        onConfirmation={handleReplaceConfirm}
        onDismiss={handleReplaceDismiss}
      />
    </div>
  );
};
